import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, HostBinding, inject, Input, OnInit } from '@angular/core';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { INotificationDTO, INotificationUserDTO } from '@core-ports/Dto/notification';
import { NotificationType } from '@core-interfaces/global';
import {
  AvatarComponent,
  BadgeComponent,
  BreadcrumbRouterComponent,
  ButtonDirective,
  ColorModeService,
  ContainerComponent,
  DropdownComponent,
  DropdownDividerDirective,
  DropdownHeaderDirective,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  FormControlDirective,
  FormDirective,
  HeaderComponent,
  HeaderNavComponent,
  HeaderTogglerDirective,
  InputGroupComponent,
  InputGroupTextDirective,
  NavItemComponent,
  NavLinkDirective,
  ProgressBarDirective,
  ProgressComponent,
  SidebarToggleDirective,
  TextColorDirective,
  ThemeDirective
} from '@coreui/angular-pro';

import { IconDirective } from '@coreui/icons-angular';
import { AuthManager } from '@infra-adapters/services/auth.service';
import { NotificationHttpService } from '@infra-adapters/services/notification.service';
import { interval, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'app-default-header',
  templateUrl: './default-header.component.html',
  standalone: true,
  imports: [HeaderComponent, ContainerComponent, HeaderTogglerDirective, SidebarToggleDirective, IconDirective, HeaderNavComponent, NavItemComponent, NavLinkDirective, RouterLink, RouterLinkActive, NgTemplateOutlet, BreadcrumbRouterComponent, ThemeDirective, DropdownComponent, DropdownToggleDirective, TextColorDirective, AvatarComponent, DropdownMenuDirective, DropdownHeaderDirective, DropdownItemDirective, BadgeComponent, DropdownDividerDirective, ProgressBarDirective, ProgressComponent, InputGroupComponent, InputGroupTextDirective, FormControlDirective, ButtonDirective, NgStyle, FormDirective]
})
export class DefaultHeaderComponent extends HeaderComponent implements OnInit {

  readonly #colorModeService = inject(ColorModeService);
  readonly colorMode = this.#colorModeService.colorMode;
  readonly #authManager = inject(AuthManager);
  readonly #router = inject(Router);
  readonly #notificationService = inject(NotificationHttpService);

  constructor() {
    super();
    this.#colorModeService.localStorageItemName.set('coreui-pro-angular-admin-template-theme-modern');
    this.#colorModeService.eventName.set('ColorSchemeChange');
  }

  @Input() sidebarId: string = 'sidebar1';


  onLogout(e: Event) {
    e.preventDefault();
    try {
      this.#authManager.cleanSession();
    } catch (err) {
      console.warn('[DefaultHeader] logout cleanSession failed', err);
    }
    this.#router.navigate(['/login']);
  }

  public newMessages = [
    {
      id: 0,
      from: 'Jessica Williams',
      avatar: '7.jpg',
      status: 'success',
      title: 'Urgent: System Maintenance Tonight',
      time: 'Just now',
      link: 'apps/email/inbox/message',
      message: 'Attention team, we\'ll be conducting critical system maintenance tonight from 10 PM to 2 AM. Plan accordingly...'
    },
    {
      id: 1,
      from: 'Richard Johnson',
      avatar: '6.jpg',
      status: 'warning',
      title: 'Project Update: Milestone Achieved',
      time: '5 minutes ago',
      link: 'apps/email/inbox/message',
      message: 'Kudos on hitting sales targets last quarter! Let\'s keep the momentum. New goals, new victories ahead...'
    },
    {
      id: 2,
      from: 'Angela Rodriguez',
      avatar: '5.jpg',
      status: 'danger',
      title: 'Social Media Campaign Launch',
      time: '1:52 PM',
      link: 'apps/email/inbox/message',
      message: 'Exciting news! Our new social media campaign goes live tomorrow. Brace yourselves for engagement...'
    },
    {
      id: 3,
      from: 'Jane Lewis',
      avatar: '4.jpg',
      status: 'info',
      title: 'Inventory Checkpoint',
      time: '4:03 AM',
      link: 'apps/email/inbox/message',
      message: 'Team, it\'s time for our monthly inventory check. Accurate counts ensure smooth operations. Let\'s nail it...'
    },
    {
      id: 3,
      from: 'Ryan Miller',
      avatar: '4.jpg',
      status: 'info',
      title: 'Customer Feedback Results',
      time: '3 days ago',
      link: 'apps/email/inbox/message',
      message: 'Our latest customer feedback is in. Let\'s analyze and discuss improvements for an even better service...'
    }
  ];

  public newNotifications: any[] = [];
  public newStatus: any[] = [];
  public newTasks: any[] = [];

  get unreadNotificationsCount(): number {
    return this.newNotifications.filter(n => !n.read_at).length;
  }

  ngOnInit(): void {
    const user = this.#authManager.getUser();
    if (user && user.id) {
      interval(30000) // Polling every 30 seconds
        .pipe(
          startWith(0),
          switchMap(() => this.#notificationService.getNotificationUser({ user_id: user.id, type: NotificationType.Administrator, limit: 10, order: { field: 'id', order: 'DESC' } }))
        )
        .subscribe({
          next: (getNotificationsUser: INotificationUserDTO[]) => {
            if (getNotificationsUser && Array.isArray(getNotificationsUser)) {
              this.newNotifications = getNotificationsUser.map((nu: INotificationUserDTO) => ({
                id: nu.id,
                title: nu.notification?.title || 'Notificación',
                message: nu.notification?.message || '',
                icon: 'cilWarning',
                color: nu.notification?.title?.includes('Crítica') ? 'danger' : 'warning',
                read_at: nu.read_at
              }));
            }
          },
          error: (err) => console.error('[DefaultHeader] Error fetching notifications', err)
        });
    }
  }

  markAsRead(id: number) {
    this.#notificationService.updateReadAt(id).subscribe({
      next: () => {
        const notification = this.newNotifications.find(n => n.id === id);
        if (notification) {
          notification.read_at = new Date();
        }
      },
      error: (err) => console.error('[DefaultHeader] Error marking as read', err)
    });
  }

}
